(function () {
  const rootSelector = '[data-learn-browse]';
  const configSelector = '[data-learn-browse-config]';
  const gridSelector = '[data-learn-browse-grid]';
  const emptySelector = '[data-learn-browse-empty]';
  const statusSelector = '[data-learn-browse-status]';
  const sourceSelectSelector = '[data-learn-browse-source-select]';
  const sourceOptionSelector = '[data-learn-browse-source]';
  const sourceOptionsSelector = '[data-learn-browse-source-options]';
  const tagSelectSelector = '[data-learn-browse-tag-select]';
  const tagOptionSelector = '[data-learn-browse-tag]';
  const tagOptionsSelector = '[data-learn-browse-tag-options]';
  const loadMoreSelector = '[data-learn-browse-load-more]';
  const loadMoreWrapSelector = '[data-learn-browse-load-more-wrap]';

  class LearnContentHubBrowse {
    constructor(root) {
      this.root = root;
      this.config = this.readConfig();
      this.sources = Array.isArray(this.config.sources) ? this.config.sources : [];
      this.grid = this.root.querySelector(gridSelector);
      this.empty = this.root.querySelector(emptySelector);
      this.status = this.root.querySelector(statusSelector);
      this.sourceSelect = this.root.querySelector(sourceSelectSelector);
      this.sourceOptions = this.root.querySelector(sourceOptionsSelector);
      this.tagSelect = this.root.querySelector(tagSelectSelector);
      this.tagOptions = this.root.querySelector(tagOptionsSelector);
      this.loadMore = this.root.querySelector(loadMoreSelector);
      this.loadMoreWrap = this.root.querySelector(loadMoreWrapSelector);
      this.requestId = 0;
      this.exhaustedSources = {};

      if (!this.grid || !this.sources.length) {
        return;
      }

      this.sourceMap = this.sources.reduce((map, source) => {
        if (source && source.handle) {
          map[source.handle] = source;
        }

        return map;
      }, {});

      this.state = this.normalizeState(this.readStateFromUrl());
      this.bindEvents();
      this.renderTagControls();
      this.syncControls();
      this.resetExhaustedSourcesFromInitial();
      this.sortInitialCards();
      this.updateEmptyState();
      this.updateLoadMoreState();

      if (!this.isDefaultState(this.state)) {
        this.loadPages({ replace: true, page: this.state.page, updateUrl: false });
      }
    }

    readConfig() {
      const configScript = this.root.querySelector(configSelector);

      if (!configScript) {
        return {};
      }

      try {
        return JSON.parse(configScript.textContent);
      } catch (error) {
        console.error('Learn Content Hub browse config is invalid.', error);
        return {};
      }
    }

    bindEvents() {
      this.root.addEventListener(
        'change',
        (event) => {
          const sourceSelect = event.target.closest(sourceSelectSelector);
          const tagSelect = event.target.closest(tagSelectSelector);

          if (sourceSelect && this.root.contains(sourceSelect)) {
            const option = sourceSelect.selectedOptions[0];

            event.preventDefault();
            event.stopImmediatePropagation();
            this.setSource(option ? option.dataset.learnBrowseSource || 'all' : 'all');
            return;
          }

          if (tagSelect && this.root.contains(tagSelect)) {
            const option = tagSelect.selectedOptions[0];

            event.preventDefault();
            event.stopImmediatePropagation();
            this.setTag(option ? option.dataset.learnBrowseTag || 'all' : 'all');
          }
        },
        true
      );

      this.root.addEventListener('click', (event) => {
        const sourceLink = event.target.closest(`${sourceOptionSelector}[href]`);
        const tagLink = event.target.closest(`${tagOptionSelector}[href]`);

        if (sourceLink && this.root.contains(sourceLink)) {
          event.preventDefault();
          this.setSource(sourceLink.dataset.learnBrowseSource || 'all');
          return;
        }

        if (tagLink && this.root.contains(tagLink)) {
          event.preventDefault();
          this.setTag(tagLink.dataset.learnBrowseTag || 'all');
        }
      });

      if (this.loadMore) {
        this.loadMore.addEventListener('click', () => this.loadNextPage());
      }

      window.addEventListener('popstate', () => {
        this.state = this.normalizeState(this.readStateFromUrl());
        this.renderTagControls();
        this.syncControls();
        this.loadPages({ replace: true, page: this.state.page, updateUrl: false });
      });
    }

    readStateFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const page = parseInt(params.get('learn_page') || '1', 10);

      return {
        blog: params.get('learn_blog') || 'all',
        tag: params.get('learn_tag') || 'all',
        page: Number.isFinite(page) && page > 0 ? page : 1,
      };
    }

    normalizeState(state) {
      const blog = state.blog === 'all' || this.sourceMap[state.blog] ? state.blog : 'all';
      const tag = this.tagExistsForBlog(state.tag, blog) ? state.tag : 'all';

      return {
        blog,
        tag,
        page: Number.isFinite(state.page) && state.page > 0 ? state.page : 1,
      };
    }

    isDefaultState(state) {
      return state.blog === 'all' && state.tag === 'all' && state.page === 1;
    }

    setSource(handle) {
      const nextState = this.normalizeState({
        blog: handle,
        tag: this.state.tag,
        page: 1,
      });

      this.state = nextState;
      this.renderTagControls();
      this.syncControls();
      this.loadPages({ replace: true, page: 1, updateUrl: true });
    }

    setTag(handle) {
      const nextState = this.normalizeState({
        blog: this.state.blog,
        tag: handle,
        page: 1,
      });

      this.state = nextState;
      this.syncControls();
      this.loadPages({ replace: true, page: 1, updateUrl: true });
    }

    loadNextPage() {
      if (this.root.classList.contains('is-browse-loading') || !this.hasMore()) {
        return;
      }

      this.state.page += 1;
      this.syncControls();
      this.loadPages({ replace: false, page: this.state.page, updateUrl: true });
    }

    async loadPages({ replace, page, updateUrl }) {
      const currentRequestId = ++this.requestId;
      const pages = replace ? this.getPageRange(page) : [page];
      const cards = [];
      let loadedCount = 0;

      this.setLoading(true);

      if (replace) {
        this.exhaustedSources = {};
      }

      try {
        for (const pageNumber of pages) {
          const batch = await this.fetchBatch(pageNumber, currentRequestId);

          if (currentRequestId !== this.requestId) {
            return;
          }

          cards.push(...batch);
          loadedCount += batch.length;
        }

        if (replace) {
          this.grid.replaceChildren(...this.dedupeCards(cards));
        } else {
          this.appendCards(cards);
        }

        if (updateUrl) {
          this.pushState();
        }

        this.announceLoaded(loadedCount, replace);
      } catch (error) {
        console.error('Learn Content Hub browse request failed.', error);
        this.announce('Could not load more articles. Please try again.');
      } finally {
        if (currentRequestId === this.requestId) {
          this.setLoading(false);
          this.updateEmptyState();
          this.updateLoadMoreState();
        }
      }
    }

    getPageRange(page) {
      return Array.from({ length: page }, (_item, index) => index + 1);
    }

    async fetchBatch(page, requestId) {
      const sources = this.getActiveSources().filter((source) => !this.exhaustedSources[source.handle]);
      const responses = await Promise.all(sources.map((source) => this.fetchSourcePage(source, page)));
      const batch = [];

      if (requestId !== this.requestId) {
        return batch;
      }

      responses.forEach((sourceResponse) => {
        this.exhaustedSources[sourceResponse.handle] = !sourceResponse.hasNext;
        batch.push(...sourceResponse.cards);
      });

      return this.sortCardsByPublishedAt(batch);
    }

    async fetchSourcePage(source, page) {
      const url = this.buildSourceUrl(source, page);
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        return {
          handle: source.handle,
          hasNext: false,
          cards: [],
        };
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector('[data-learn-fragment]');
      const cardNodes = fragment ? Array.from(fragment.querySelectorAll('[data-learn-card]')) : [];

      return {
        handle: source.handle,
        hasNext: Boolean(fragment && fragment.dataset.hasNext === 'true'),
        cards: cardNodes.map((card) => this.decorateCard(card, source)),
      };
    }

    buildSourceUrl(source, page) {
      const basePath = source.url.replace(/\/$/, '');
      const tagPath = this.state.tag !== 'all' ? `${basePath}/tagged/${encodeURIComponent(this.state.tag)}` : basePath;
      const url = new URL(tagPath, window.location.origin);

      url.searchParams.set('page', page);
      url.searchParams.set('section_id', this.config.fragmentSectionId);

      return url;
    }

    decorateCard(card, source) {
      card.dataset.blogHandle = source.handle;
      card.dataset.blogTitle = source.title || '';
      card.dataset.contentType = source.contentType || 'article';
      card.dataset.contentTypeLabel = source.contentTypeLabel || 'Article';
      card.dataset.sourceLabel = source.sourceLabel || source.title || '';
      card.style.setProperty('--learn-badge-background', source.badgeBackground || '#eef2ff');
      card.style.setProperty('--learn-badge-color', source.badgeTextColor || '#3730a3');

      const badge = card.querySelector('.learn-content-hub__badge');
      const sourceLabel = card.querySelector('.learn-content-hub__source');

      if (badge) {
        badge.textContent = source.contentTypeLabel || 'Article';
      }

      if (sourceLabel) {
        sourceLabel.textContent = source.sourceLabel || source.title || '';
      }

      return card;
    }

    appendCards(cards) {
      const existingUrls = this.getExistingArticleUrls();
      const uniqueCards = cards.filter((card) => {
        const url = card.dataset.articleUrl;

        if (!url || existingUrls.has(url)) {
          return false;
        }

        existingUrls.add(url);
        return true;
      });

      this.grid.append(...uniqueCards);
    }

    dedupeCards(cards) {
      const seen = new Set();

      return cards.filter((card) => {
        const url = card.dataset.articleUrl;

        if (!url || seen.has(url)) {
          return false;
        }

        seen.add(url);
        return true;
      });
    }

    sortCardsByPublishedAt(cards) {
      return cards.sort((a, b) => {
        const aDate = parseInt(a.dataset.publishedAt || '0', 10);
        const bDate = parseInt(b.dataset.publishedAt || '0', 10);

        return bDate - aDate;
      });
    }

    sortInitialCards() {
      const cards = Array.from(this.grid.querySelectorAll('[data-learn-card]'));

      if (cards.length > 1) {
        this.grid.replaceChildren(...this.sortCardsByPublishedAt(cards));
      }
    }

    getExistingArticleUrls() {
      return new Set(
        Array.from(this.grid.querySelectorAll('[data-article-url]')).map((card) => card.dataset.articleUrl)
      );
    }

    getActiveSources() {
      if (this.state.blog === 'all') {
        return this.sources;
      }

      return this.sourceMap[this.state.blog] ? [this.sourceMap[this.state.blog]] : [];
    }

    getAvailableTags(blog) {
      const tags = [];
      const seen = new Set();
      const sources = blog === 'all' ? this.sources : this.sourceMap[blog] ? [this.sourceMap[blog]] : [];

      sources.forEach((source) => {
        (source.tags || []).forEach((tag) => {
          if (!tag.handle || seen.has(tag.handle)) {
            return;
          }

          seen.add(tag.handle);
          tags.push(tag);
        });
      });

      return tags;
    }

    tagExistsForBlog(tag, blog) {
      if (!tag || tag === 'all') {
        return true;
      }

      return this.getAvailableTags(blog).some((availableTag) => availableTag.handle === tag);
    }

    renderTagControls() {
      const tags = this.getAvailableTags(this.state.blog);

      if (this.tagSelect) {
        this.tagSelect.replaceChildren(
          this.createTagOption({ handle: 'all', label: this.config.allTagsLabel || 'All categories' })
        );

        tags.forEach((tag) => {
          this.tagSelect.append(this.createTagOption(tag));
        });
      }

      if (this.tagOptions) {
        this.tagOptions.replaceChildren(
          this.createTagLink({ handle: 'all', label: this.config.allTagsLabel || 'All categories' })
        );

        tags.forEach((tag) => {
          this.tagOptions.append(this.createTagLink(tag));
        });
      }
    }

    createTagOption(tag) {
      const option = document.createElement('option');

      option.value = this.buildLearnUrl({ ...this.state, tag: tag.handle, page: 1 });
      option.dataset.learnBrowseTag = tag.handle;
      option.textContent = tag.label;

      return option;
    }

    createTagLink(tag) {
      const link = document.createElement('a');

      link.href = this.buildLearnUrl({ ...this.state, tag: tag.handle, page: 1 });
      link.className = 'learn-content-hub__chip';
      link.dataset.learnBrowseTag = tag.handle;
      link.textContent = tag.label;

      return link;
    }

    syncControls() {
      this.syncSourceControls();
      this.syncTagControls();
    }

    syncSourceControls() {
      if (this.sourceSelect) {
        Array.from(this.sourceSelect.options).forEach((option) => {
          const handle = option.dataset.learnBrowseSource || 'all';

          option.value = this.buildLearnUrl({ ...this.state, blog: handle, page: 1 });
          option.selected = handle === this.state.blog;
        });
      }

      Array.from(this.root.querySelectorAll(`${sourceOptionSelector}[href]`)).forEach((link) => {
        const handle = link.dataset.learnBrowseSource || 'all';
        const isActive = handle === this.state.blog;

        link.href = this.buildLearnUrl({ ...this.state, blog: handle, page: 1 });
        link.classList.toggle('is-active', isActive);
        this.setCurrentAttribute(link, isActive);
      });
    }

    syncTagControls() {
      if (this.tagSelect) {
        Array.from(this.tagSelect.options).forEach((option) => {
          const handle = option.dataset.learnBrowseTag || 'all';

          option.value = this.buildLearnUrl({ ...this.state, tag: handle, page: 1 });
          option.selected = handle === this.state.tag;
        });
      }

      Array.from(this.root.querySelectorAll(`${tagOptionSelector}[href]`)).forEach((link) => {
        const handle = link.dataset.learnBrowseTag || 'all';
        const isActive = handle === this.state.tag;

        link.href = this.buildLearnUrl({ ...this.state, tag: handle, page: 1 });
        link.classList.toggle('is-active', isActive);
        this.setCurrentAttribute(link, isActive);
      });
    }

    setCurrentAttribute(element, isActive) {
      if (isActive) {
        element.setAttribute('aria-current', 'page');
      } else {
        element.removeAttribute('aria-current');
      }
    }

    buildLearnUrl(state) {
      const url = new URL(this.config.learnPageUrl || window.location.pathname, window.location.origin);

      url.searchParams.set('learn_blog', state.blog || 'all');
      url.searchParams.set('learn_tag', state.tag || 'all');
      url.searchParams.set('learn_page', state.page || 1);

      return `${url.pathname}${url.search}`;
    }

    pushState() {
      window.history.pushState({ learnContentHubBrowse: true }, '', this.buildLearnUrl(this.state));
    }

    resetExhaustedSourcesFromInitial() {
      this.sources.forEach((source) => {
        this.exhaustedSources[source.handle] = Number(source.articleCount || 0) <= Number(this.config.pageSize || 15);
      });
    }

    hasMore() {
      return this.getActiveSources().some((source) => !this.exhaustedSources[source.handle]);
    }

    updateLoadMoreState() {
      if (!this.loadMore || !this.loadMoreWrap) {
        return;
      }

      this.loadMoreWrap.hidden = !this.hasMore();
      this.loadMore.disabled = this.root.classList.contains('is-browse-loading') || !this.hasMore();
    }

    updateEmptyState() {
      if (!this.empty) {
        return;
      }

      this.empty.hidden = Boolean(this.grid.querySelector('[data-learn-card]'));
    }

    setLoading(isLoading) {
      this.root.classList.toggle('is-browse-loading', isLoading);

      if (this.loadMore) {
        this.loadMore.disabled = isLoading;
      }
    }

    announceLoaded(count, replace) {
      if (replace) {
        this.announce(`Loaded ${count} articles.`);
      } else {
        this.announce(`Loaded ${count} more articles.`);
      }
    }

    announce(message) {
      if (this.status) {
        this.status.textContent = message;
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(rootSelector).forEach((root) => new LearnContentHubBrowse(root));
  });
})();
